import {Component, ElementRef, Inject, ViewChild} from '@angular/core';
import {
    InspectorEvent,
    ViewConfigPayload,
    WORKBENCH_VIEW_CONFIG,
    WorkbenchViewConfig
} from '../../shared/types';
import {BlobId, VideoRecordingId} from '@webmate-web-core/shared/types/ids';
import {WorkbenchViewComponent} from '../../shared/components/viewcontainers/workbench-view.component';
import {WorkbenchLayoutManager} from '../../shared/services/workbench-layout-manager.service';
import {VideoRecordingService} from '@webmate-web-core/shared/services/video-recording.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {WorkbenchCommandRegistry} from '../../shared/services/workbench-command-registry.service';
import {InspectorService} from '../../shared/services/inspector.service';
import {BlobUrlService} from '@webmate-web-core/shared/services/blob-url.service';
import {WorkbenchContextService} from '../../shared/services/workbench-context.service';
import {IndexedDbService} from '../../shared/services/indexed-db.service';
import {ProjectId} from '../../../report/report_types';
import {SearchService} from '@webmate-web-core/shared/services/search.service';
import {catchError, distinctUntilChanged, startWith, switchMap, take, filter, tap} from 'rxjs/operators';
import {interval, Observable, of} from 'rxjs';

export interface VideoViewConfig extends ViewConfigPayload {
    data: {
        videoId?: VideoRecordingId;
        name: string;
        duration?: string;
        size?: string;
        format?: string;
        /** IndexedDB blob ID for a locally dropped file — survives page reloads. */
        localVideoBlobId?: BlobId;
    };
}

@UntilDestroy()
@Component({
    selector: 'wm-workbench-video-viewer',
    templateUrl: './workbench-video-viewer.component.html',
    styleUrl: './workbench-video-viewer.component.less'
})
export class WorkbenchVideoViewerComponent extends WorkbenchViewComponent {
    projectId: ProjectId;
    videoId: VideoRecordingId;
    videoName: string;
    videoUrl?: string;
    videoFormat?: string;
    blobId?: BlobId;
    fetchFailed: boolean = false;

    inspectorIsOpen: boolean = false;

    videoInProcessingState: boolean = false;
    loadingVideo: boolean = true;
    loadingVideoError: boolean = false;

    @ViewChild('videoPlayer') videoPlayer: ElementRef;

    constructor(layoutManager: WorkbenchLayoutManager,
                @Inject(WORKBENCH_VIEW_CONFIG) public config: WorkbenchViewConfig<VideoViewConfig>,
                private workbenchCommandRegistry: WorkbenchCommandRegistry,
                private readonly workbenchContextService: WorkbenchContextService,
                private inspectorService: InspectorService,
                private videoService: VideoRecordingService,
                private searchService: SearchService,
                private blobUrlService: BlobUrlService,
                private indexedDbService: IndexedDbService) {
        super(layoutManager, config);
    }

    ngOnInit() {
        this.projectId = this.workbenchContextService.getProjectId()
        this.videoId = this.config.payload?.data.videoId;
        this.videoName = this.config.payload?.data.name;

        const localVideoBlobId = this.config.payload?.data.localVideoBlobId;
        if (localVideoBlobId) {
            // Locally dropped file — retrieve from IndexedDB so it survives page reloads.
            this.videoFormat = this.config.payload?.data.format ? `video/${this.config.payload?.data.format}` : 'video/mp4';
            this.indexedDbService.getBlob(localVideoBlobId).subscribe(blob => {
                if (blob) {
                    this.videoUrl = URL.createObjectURL(blob);
                } else {
                    this.fetchFailed = true;
                }
                this.loadingVideo = false;
            });
            return;
        }

        if (this.videoId) {
            const url = this.videoService.getVideoUrl(this.videoId);
            this.videoFormat = this.config.payload?.data.format ? `video/${this.config.payload?.data.format}` : 'video/mp4';
            // polling to find if processing completed in the backend
            interval(2000).pipe(
                startWith(0),
                switchMap(() => this.videoService.getVideoRecording(this.config.payload!.data.videoId)),
                tap(vr => {
                    if (vr.recordingState !== 'stopped') {
                        this.videoInProcessingState = true;
                    }
                }),
                filter(vr => vr.recordingState === 'stopped'),
                tap(_ => {
                    this.videoInProcessingState = false;
                }),
                take(1),
                switchMap(() => this.blobUrlService.loadAsObjectUrl(url)),
                take(1),
            ).subscribe(({ url: objectUrl, blobId }) => {
                this.blobUrlService.releaseBlobUrl(this.blobId, this.videoUrl).subscribe();
                this.videoUrl = objectUrl;
                this.blobId = blobId;
            });

            if (this.projectId) {
                this.searchService.getDeletedNotification(this.projectId).pipe(
                    untilDestroyed(this),
                    tap(id => {
                        if (this.videoId === id) {
                            this.layoutManager.closeView(this.config.id);
                        }
                    })
                ).subscribe();
                this.searchService.getIndexingNotifications(this.projectId).pipe(
                    untilDestroyed(this),
                    switchMap(() => {
                        return this.videoService.getVideoRecording(this.videoId);
                    })
                ).subscribe(video => {
                    if (video.metadata?.name) {
                        this.layoutManager.changeViewName(this.config.id, video.metadata.name);
                        this.videoName = video.metadata.name;
                    }
                });
            }
            // Gets the name and sets it for the view
            this.videoService.getVideoRecording(this.videoId).subscribe(video => {
                if (video.metadata?.name) {
                    this.layoutManager.changeViewName(this.config.id, video.metadata.name);
                    this.videoName = video.metadata.name;
                }
            });
        }
    }

    ngAfterViewInit() {
        this.videoPlayer.nativeElement.onloadedmetadata = () => {
            // Forcing calculation of video length by seeking to end
            this.videoPlayer.nativeElement.currentTime = Number.MAX_SAFE_INTEGER;
            this.videoPlayer.nativeElement.ondurationchange = () => {
                this.videoPlayer.nativeElement.currentTime = 0;
                this.videoPlayer.nativeElement.ondurationchange = null;
            };
        };
        this.videoPlayer.nativeElement.onloadeddata = () => {
            this.layoutManager.subscribeActiveViewId$().pipe(
                untilDestroyed(this), distinctUntilChanged()
            ).subscribe(activeView => {
                if (activeView === this.viewId && !this.inspectorIsOpen){
                    this.openVideoRecordingTools();
                    this.inspectorIsOpen = true;
                }
                if (activeView !== this.viewId && this.inspectorIsOpen){
                    this.closeVideoRecordingTools();
                    this.inspectorIsOpen = false;
                }
            });
        };
    }

    override beforeViewClose() {
        const localVideoBlobId = this.config.payload?.data.localVideoBlobId;
        if (localVideoBlobId) {
            // Revoke the transient object URL and remove the file data from IndexedDB.
            if (this.videoUrl) {
                URL.revokeObjectURL(this.videoUrl);
            }
            this.indexedDbService.removeBlob(localVideoBlobId).subscribe();
        } else {
            this.blobUrlService.releaseBlobUrl(this.blobId, this.videoUrl).subscribe();
        }
        this.closeVideoRecordingTools();
    }

    openVideoRecordingTools() {
        setTimeout(() => {
            this.workbenchCommandRegistry.executeCommand("videoRecording:openVideoRecordingTools", {
                type: 'OpenVideoRecording',
                id: this.config.payload?.data.videoId,
                name: this.videoName,
                videoUrl: this.videoUrl!,
                duration: this.config.payload?.data.duration ? Number(this.config.payload?.data.duration) : undefined,
                size: this.config.payload?.data.size ? Number(this.config.payload?.data.size) : undefined,
                format: this.videoFormat,
            });
        }, 5);
    }

    closeVideoRecordingTools(){
        const event: InspectorEvent = {type: "ClearInspector"};
        this.inspectorService.raiseInspectorEvent(event);
    }

    onVideoLoaded() {
        this.loadingVideo = false;
    }

    onVideoLoadError() {
        this.loadingVideoError = true;
        this.loadingVideo = false;
    }
}
